"use client";

import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";

/**
 * One-time announcement strip shown to authenticated users after a
 * release. Dismissal is persisted to localStorage under a versioned key,
 * so a future banner can reuse this component by bumping the version.
 *
 * Renders nothing for logged-out visitors and during the auth-loading
 * window -- avoids a flash on marketing pages and a flicker before the
 * dismissal flag is read on app pages.
 */

// Bump the suffix (vN) to re-surface for everyone after a future update.
const STORAGE_KEY = "maren_update_banner_dismissed_v1";

export default function UpdateBanner() {
  const { session, loading } = useAuth();
  const [ready, setReady] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Read localStorage once on mount; gate render on `ready` so we never
  // flash the banner for users who've already dismissed it.
  useEffect(() => {
    try {
      if (typeof window !== "undefined" && localStorage.getItem(STORAGE_KEY) === "1") {
        setDismissed(true);
      }
    } catch {
      // Privacy modes can throw on storage access; default to "not dismissed".
    }
    setReady(true);
  }, []);

  function handleDismiss() {
    setDismissed(true);
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      // Best-effort -- if storage is blocked, the banner will reappear
      // next visit, which is acceptable.
    }
  }

  if (!ready || loading || !session || dismissed) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="border-b border-maren-gold/40 dark:border-maren-gold/30 bg-maren-gold/10 dark:bg-maren-gold/15"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2 flex items-start gap-3">
        <p className="flex-1 text-sm leading-snug text-maren-charcoal/80 dark:text-maren-stone/80">
          We&apos;ve shipped some updates and bug fixes this week. If anything looks
          off, please email us at{" "}
          <a
            href="mailto:support@joinmaren.com"
            className="font-semibold text-maren-green dark:text-maren-gold hover:underline"
          >
            support@joinmaren.com
          </a>
          .
        </p>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss announcement"
          className="shrink-0 -mr-1 text-maren-charcoal/55 dark:text-maren-stone/55 hover:text-maren-charcoal dark:hover:text-maren-stone text-lg leading-none px-2"
        >
          ×
        </button>
      </div>
    </div>
  );
}
